import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import "./Button.css";

type Variant = "primary" | "secondary" | "ghost";

interface BaseProps {
  variant?: Variant;
  block?: boolean;
}

interface LinkButtonProps
  extends BaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children"> {
  to: string;
  external?: boolean;
  children: ReactNode;
}

interface ActionButtonProps
  extends BaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  to?: undefined;
  children: ReactNode;
}

function classesFor(variant: Variant, block: boolean | undefined, extra?: string) {
  return ["btn", `btn-${variant}`, block ? "btn-block" : "", extra].filter(Boolean).join(" ");
}

/**
 * DVS-014 (GHSA-jjmj-jmhj-qwj2, GHSA-wrjc-x8rr-h8h6) mitigation. Every `to` value
 * passed to CtaLink today is a static literal, so this isn't currently exploitable —
 * but this is the site's one reusable internal-link primitive, and the upstream
 * react-router fix requires a v7 migration tracked separately. Collapse any leading
 * run of `/` or `\` to a single `/` so a value that ever does come from dynamic
 * content can't be parsed by the browser or react-router as a protocol-relative
 * or absolute redirect target. No effect on any path that already starts with one `/`.
 */
function safeInternalPath(path: string): string {
  return path.replace(/^[/\\]+/, "/");
}

/** Use for navigation — internal routes use React Router, external/mailto links use a plain anchor. */
export function CtaLink({
  to,
  variant = "primary",
  block,
  external,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  const classes = classesFor(variant, block, className);
  if (external || to.startsWith("http") || to.startsWith("mailto:")) {
    return (
      <a className={classes} href={to} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link className={classes} to={safeInternalPath(to)} {...rest}>
      {children}
    </Link>
  );
}

/** Use only for real in-page actions (form submit, toggle) — never for navigation. */
export function ActionButton({
  variant = "primary",
  block,
  className,
  children,
  ...rest
}: ActionButtonProps) {
  return (
    <button className={classesFor(variant, block, className)} {...rest}>
      {children}
    </button>
  );
}
